import { useNavigate } from "react-router-dom";

import { useEffect, useId, useMemo, useRef, useState } from "react";
import { getSellerPath } from "@/lib/routing/portal-urls";

import { useTranslations } from "@/lib/i18n/intl";
import { zodResolver } from "@hookform/resolvers/zod";
import type { DefaultValues } from "react-hook-form";
import { useForm, type Resolver } from "react-hook-form";
import { keys } from "@/lib/i18n/portal";
import type { CategoryView, ProductView } from "@/lib/api/types";
import type { Locale } from "@/lib/i18n/config";
import {
  buildProductDetailsSchema,
  type ProductDetailsFormValues,
} from "@/modules/products/components/productDetailsSchema";
import { useCreateProductMutation } from "@/modules/products/services/useCreateProductMutation";
import { useUpdateProductMutation } from "@/modules/products/services/useUpdateProductMutation";
import {
  Alert,
  DiscardChangesDialog,
  FormActionButtons,
  SectionCard,
  Segmented,
} from "@a2z/ui";
import {
  ProductDetailsCoreFields,
  ProductPricingFields,
} from "./ProductDetailsFields";
import { InfoIcon, TagIcon } from "./product-edit-icons";
import { useFloatingActionBar } from "@/lib/hooks/useFloatingActionBar";
import { useFormSafety } from "@/lib/navigation/useFormSafety";
import { useNavigationGuard } from "@/lib/navigation/navigationGuard";

type FormProps = {
  locale: Locale;
  storeSlug: string;
  mode: "create" | "edit";
  productId?: string;
  categories: CategoryView[];
  submitLabel?: string;
  onSuccess?: (product: ProductView) => void;
  hideCancel?: boolean;
  onCancel?: () => void;
  onDirtyChange?: (isDirty: boolean) => void;
  enableNavigationGuard?: boolean;
  /** When true, render the action buttons as a sticky bottom bar within the scroll container (for use inside ModalSheet). */
  stickyActions?: boolean;
  /** Suppress all inline action UI (page-level SaveBar takes over). */
  hideActions?: boolean;
  /**
   * True when the product has real (size/colour) variants. Discount then lives
   * per-variant, so the product-level "Discounted price" field is hidden — the
   * product-level pricing only stands in for the default variant of an
   * options-less product.
   */
  hasVariants?: boolean;
  defaultValues?: {
    nameAr?: string;
    nameEn?: string | null;
    shortDescriptionAr?: string | null;
    shortDescriptionEn?: string | null;
    categoryIds?: string[];
    price?: number;
    discountedPrice?: number | null;
    cost?: number | null;
    stockCount?: number;
    forceOutOfStock?: boolean;
    isActive?: boolean;
  };
  /**
   * When provided, renders inside the Product details card body when the
   * "Rich content" tab is active. Typically a `<ProductContentForm />`
   * (its own internal form) — not nested inside our `<form>` tag.
   */
  richContent?: React.ReactNode;
};

/** Trioty card chrome for the edit section cards on mobile (≤lg) only. */
const MOBILE_CARD_CHROME =
  "max-lg:!rounded-[18px] max-lg:!border-[color:var(--hair)] max-lg:!bg-[color:var(--soft)] max-lg:[&>header]:!items-center max-lg:[&>header>span]:hidden max-lg:[&>header>div:first-of-type>span:nth-child(2)]:hidden";

const trimOrNull = (value: string | null | undefined): string | null => {
  const trimmed = value?.trim() ?? "";
  return trimmed.length > 0 ? trimmed : null;
};

export function ProductDetailsForm({
  locale,
  storeSlug,
  mode,
  productId,
  categories,
  submitLabel,
  onSuccess,
  defaultValues,
  hideCancel = false,
  onCancel,
  onDirtyChange,
  enableNavigationGuard = true,
  stickyActions = false,
  hideActions = false,
  hasVariants = false,
  richContent,
}: FormProps) {
  const navigate = useNavigate();
  const t = useTranslations();
  const errorRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const formId = useId();
  const actionRef = useRef<HTMLDivElement | null>(null);
  const initialValues = useMemo<DefaultValues<ProductDetailsFormValues>>(() => {
    const categoryIds = defaultValues?.categoryIds ?? [];
    return {
      nameAr: defaultValues?.nameAr ?? "",
      nameEn: defaultValues?.nameEn ?? "",
      shortDescriptionAr: defaultValues?.shortDescriptionAr ?? "",
      shortDescriptionEn: defaultValues?.shortDescriptionEn ?? "",
      categoryIds,
      price:
        typeof defaultValues?.price === "number"
          ? defaultValues.price
          : undefined,
      discountedPrice:
        typeof defaultValues?.discountedPrice === "number"
          ? defaultValues.discountedPrice
          : undefined,
      cost:
        typeof defaultValues?.cost === "number"
          ? defaultValues.cost
          : undefined,
      stockCount:
        typeof defaultValues?.stockCount === "number"
          ? defaultValues.stockCount
          : mode === "create"
            ? 1
            : undefined,
      forceOutOfStock: defaultValues?.forceOutOfStock ?? false,
      isActive: defaultValues?.isActive ?? true,
    };
  }, [
    mode,
    defaultValues?.categoryIds,
    defaultValues?.shortDescriptionAr,
    defaultValues?.shortDescriptionEn,
    defaultValues?.discountedPrice,
    defaultValues?.cost,
    defaultValues?.forceOutOfStock,
    defaultValues?.isActive,
    defaultValues?.nameAr,
    defaultValues?.nameEn,
    defaultValues?.price,
    defaultValues?.stockCount,
  ]);
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isSubmitting, dirtyFields },
  } = useForm<ProductDetailsFormValues>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(buildProductDetailsSchema(t) as any) as Resolver<ProductDetailsFormValues>,
    defaultValues: initialValues,
  });
  const guard = useNavigationGuard();
  const { promptDiscard, discardDialogState } = useFormSafety({
    isDirty,
    onDiscard: () => {
      reset();
      setError(null);
    },
    enableNavigationGuard,
  });
  const discardPrompt = guard?.promptDiscard ?? promptDiscard;
  const shouldFloatActions = useFloatingActionBar(
    actionRef,
    isDirty && !hideCancel
  );
  const saveLabel = submitLabel ?? t(keys.common.save);
  const { mutateAsync: createProduct } = useCreateProductMutation();
  const { mutateAsync: updateProduct } = useUpdateProductMutation();
  const handleCancel = () => {
    discardPrompt(() => {
      reset();
      setError(null);
      if (onCancel) {
        onCancel();
      }
    });
  };

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [error]);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    reset(initialValues);
  }, [initialValues, reset]);

  const submitForm = handleSubmit(async (values) => {
    // The edit page submits every section form at once; skip the ones with no
    // changes so a clean section never triggers a no-op save or success toast.
    if (mode === "edit" && !isDirty) {
      return;
    }
    setError(null);
    const nameEn = values.nameEn?.trim() ?? "";
    const shortDescriptionAr = trimOrNull(values.shortDescriptionAr);
    const shortDescriptionEn = trimOrNull(values.shortDescriptionEn);

    const payload = {
      nameAr: values.nameAr,
      nameEn: nameEn.length > 0 ? nameEn : null,
      categoryIds: values.categoryIds ?? [],
      isActive: values.isActive,
    };
    const initialVariant = {
      price: values.price,
      discountedPrice: values.discountedPrice ?? null,
      cost: values.cost ?? null,
      stockCount: values.stockCount,
      forceOutOfStock: values.forceOutOfStock,
    };

    let product: ProductView;
    try {
      if (mode === "create") {
        // A new product is always single-variant — its pricing seeds the
        // default variant.
        product = await createProduct({
          storeSlug,
          ...payload,
          initialVariant,
          shortDescriptionAr,
          shortDescriptionEn,
        });
      } else {
        const updatePayload: Parameters<typeof updateProduct>[0] = {
          storeSlug,
          productId: productId ?? "",
          ...payload,
        };
        // Only an options-less product carries product-level pricing (→ default
        // variant). Once real variants exist, pricing is per-variant and the
        // details form must not touch it.
        if (!hasVariants) {
          updatePayload.initialVariant = initialVariant;
        }
        if (dirtyFields.shortDescriptionAr) {
          updatePayload.shortDescriptionAr = shortDescriptionAr;
        }
        if (dirtyFields.shortDescriptionEn) {
          updatePayload.shortDescriptionEn = shortDescriptionEn;
        }
        product = await updateProduct(updatePayload);
      }
    } catch (caughtError) {
      setError(
        caughtError instanceof Error ? caughtError.message : t(keys.errors.generic),
      );
      return;
    }

    reset(values);
    if (onSuccess) {
      onSuccess(product);
    } else {
      navigate(
        `${getSellerPath(locale, storeSlug)}/products?notice=product_saved`
      , { replace: true });
    }
  });

  const [tab, setTab] = useState<"general" | "rich">("general");

  return (
    <>
      {/* Product details SectionCard sits OUTSIDE the form tag because
          the "Rich content" tab body renders its own internal <form>
          (ProductContentForm). HTML doesn't allow nested forms. The
          Controllers inside ProductDetailsCoreFields stay bound via
          react-hook-form's `control` regardless of DOM nesting. */}
      <SectionCard
        id="details"
        variant="flat"
        className={MOBILE_CARD_CHROME}
        title={t(keys.products.createStepDetails)}
        subtitle={t(keys.products.editDetailsSubtitle)}
        icon={<InfoIcon />}
        isDirty={isDirty}
        right={
          richContent ? (
            <Segmented
              value={tab}
              onChange={(value) => setTab(value as "general" | "rich")}
              options={[
                { value: "general", label: t(keys.products.editTabGeneral) },
                { value: "rich", label: t(keys.products.editTabContent) },
              ]}
            />
          ) : undefined
        }
      >
        {tab === "general" || !richContent ? (
          <ProductDetailsCoreFields
            locale={locale}
            categories={categories}
            control={control}
            errors={errors}
          />
        ) : (
          richContent
        )}
      </SectionCard>

      <form id={formId} onSubmit={submitForm} className="flex flex-col gap-5">
        {error ? (
          <div ref={errorRef}>
            <Alert message={error} helperText={t(keys.errors.recovery)} />
          </div>
        ) : null}

        {/* Product-level pricing exists only while the product has no variants
            — it edits the (hidden) default variant and reads to the seller as
            simply "the product's price". Once real variants exist, pricing is
            entirely per-variant and this section disappears. */}
        {!hasVariants ? (
          <SectionCard
            variant="flat"
            className={MOBILE_CARD_CHROME}
            title={t(keys.products.sectionPricing)}
            subtitle={t(keys.products.editPricingSubtitle)}
            icon={<TagIcon />}
          >
            <ProductPricingFields
              locale={locale}
              control={control}
              errors={errors}
            />
          </SectionCard>
        ) : null}

        {hideActions ? null : stickyActions ? (
          <div className="sticky bottom-0 -mx-4 mt-6 border-t border-[color:var(--border-light)] bg-[color:var(--surface)] px-4 py-3 sm:-mx-6 sm:px-6">
            <FormActionButtons
              formId={formId}
              cancelLabel={t(keys.common.cancel)}
              saveLabel={saveLabel}
              loadingLabel={t(keys.common.loading)}
              onCancel={handleCancel}
              isSaving={isSubmitting}
              hideCancel={hideCancel}
            />
          </div>
        ) : (
          // Sentinel div tracked by IntersectionObserver so the floating
          // save bar appears when the form is scrolled out of view. No
          // visible Save/Cancel inside the card body — only the floating
          // pill shown below when dirty.
          <div
            ref={actionRef}
            aria-hidden="true"
            className="pointer-events-none h-0 w-0"
          />
        )}
      </form>
      {!hideActions && shouldFloatActions ? (
        <div
          className={`fixed inset-x-0 bottom-[calc(4rem+env(safe-area-inset-bottom))] z-40 border-t border-[color:var(--border-light)] bg-[color:var(--surface)] px-4 py-3 shadow-[var(--shadow-sm)] transition-all duration-300 ease-out transform lg:bottom-0 ${
            shouldFloatActions
              ? "opacity-100 translate-y-0"
              : "opacity-50 translate-y-4"
          }`}
        >
          <div className="mx-auto max-w-[640px]">
            <FormActionButtons
              formId={formId}
              cancelLabel={t(keys.common.cancel)}
              saveLabel={saveLabel}
              loadingLabel={t(keys.common.loading)}
              onCancel={handleCancel}
              isSaving={isSubmitting}
              hideCancel={hideCancel}
              centered
            />
          </div>
        </div>
      ) : null}
      {!guard ? (
        <DiscardChangesDialog
          {...discardDialogState}
          title={t(keys.forms.discardChangesTitle)}
          body={t(keys.forms.discardChangesBody)}
          discardLabel={t(keys.forms.discardChangesDiscard)}
          continueLabel={t(keys.forms.discardChangesContinue)}
        />
      ) : null}
    </>
  );
}
